<template>
  <div class="analytics-view view">
    <!-- In-view permission guard: the router has no permission middleware, so
         menu visibility alone does not stop a typed URL. Same pattern as
         DrugRegister.vue. The Kraken endpoints 403 regardless. -->
    <div v-if="!canView" class="panel analytics-panel">
      <h3 style="margin-top: 4em">You do not have permission to view analytics.</h3>
    </div>

    <div v-else class="panel analytics-panel">
      <div class="analytics-header">
        <div class="analytics-tabs">
          <h3 class="tab selected-tab">Governance</h3>
          <h3 class="tab" @click="$router.push('/analytics/theatre')">Theatre &amp; Ward</h3>
        </div>
        <div class="analytics-filters">
          <Calendar v-model="dateRange" selectionMode="range" placeholder="Date range..." dateFormat="dd/mm/yy" />
          <button class="button submit-button" @click="fetchAll()">Apply</button>
        </div>
      </div>

      <div class="tile-row" v-if="summary">
        <HeroFigure
          v-bind="summary.hero"
          :spark="summary.hero.spark"
        />
        <StatTile
          v-for="tile in summary.tiles"
          :key="tile.key"
          v-bind="tile"
          :downIsGood="tile.key !== 'stocktake_compliance'"
        />
      </div>
      <div class="tile-row-placeholder" v-else-if="loading.summary">
        <i class="pi pi-spin pi-spinner"></i>
      </div>

      <div class="chart-section">
        <h4 class="chart-section-label">Register integrity</h4>
        <div class="chart-grid">
          <ChartCard
            title="Register variance"
            subtitle="Units per day, counted against the register"
            type="bar"
            :options="varianceOptions"
            :categories="panels.variance.categories"
            :series="panels.variance.series"
            :loading="loading.variance"
          />
          <ChartCard
            title="Register movements by action"
            subtitle="Transactions per day"
            type="bar"
            :options="movementOptions"
            :categories="panels.movements.categories"
            :series="movements.series"
            :loading="loading.movements"
          />
        </div>
      </div>

      <div class="chart-section">
        <h4 class="chart-section-label">Compliance</h4>
        <div class="chart-grid">
          <ChartCard
            title="Alerts raised and dismissed"
            subtitle="Count per day"
            type="bar"
            :options="alertOptions"
            :categories="panels.alerts.categories"
            :series="panels.alerts.series"
            :loading="loading.alerts"
          />
          <ChartCard
            title="Unwitnessed share by transaction type"
            subtitle="Share of transactions completed without a witness"
            type="bar"
            :options="unwitnessedOptions"
            :categories="panels.unwitnessed.categories"
            :series="panels.unwitnessed.series"
            :loading="loading.unwitnessed"
          />
          <ChartCard
            title="Top drugs by units out, per ISO week"
            subtitle="Units leaving the register each week"
            type="heatmap"
            height="320"
            :options="heatmapChartOptions"
            :categories="panels.drugWeeks.categories"
            :series="drugWeekSeries"
            :loading="loading.drugWeeks"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import HeroFigure from '@/components/analytics/HeroFigure.vue';
import StatTile from '@/components/analytics/StatTile.vue';
import ChartCard from '@/components/analytics/ChartCard.vue';
import {
  barOptions,
  divergingBarOptions,
  endLabels,
  foldSeries,
  heatmapOptions,
  withDateAxis,
  withIntegerAxis,
  withoutLegend,
  withPercentAxis,
} from '@/services/chartTheme';
import { defaultRange, loadSequentially } from '@/services/analyticsApi';
import '@/assets/css/analytics.scss';

export default {
  name: 'GovernanceDashboard',
  components: { HeroFigure, StatTile, ChartCard },
  data() {
    return {
      dateRange: defaultRange(),
      runId: 0,
      loading: {
        summary: false,
        variance: false,
        movements: false,
        alerts: false,
        unwitnessed: false,
        drugWeeks: false,
      },
      summary: null,
      panels: {
        variance: { categories: [], series: [] },
        movements: { categories: [], series: [] },
        unwitnessed: { categories: [], series: [] },
        alerts: { categories: [], series: [] },
        drugWeeks: { categories: [], series: [] },
      },
    };
  },
  computed: {
    canView() {
      return (
        this.$store.state.user.analytics_active &&
        this.$store.state.user.role.permissions.includes('can_view_transactions_report')
      );
    },
    varianceOptions() {
      return withoutLegend(withDateAxis(divergingBarOptions()));
    },
    /**
     * The endpoint returns one series per action, and there are more actions
     * than the palette has slots - fold the tail so no two series share a
     * colour. Both the series and the colours come from the one call.
     */
    movements() {
      return foldSeries(this.panels.movements.series);
    },
    movementOptions() {
      return {
        ...withDateAxis(barOptions({ stacked: true })),
        colors: this.movements.colors,
      };
    },
    /**
     * Grouped columns, not lines. Alerts are sparse whole-number events, and a
     * smoothed line between 0 and 1 drew a curve through values that never
     * occurred (and dipped below zero on the way).
     */
    alertOptions() {
      const max = Math.max(
        1,
        ...this.panels.alerts.series.flatMap((s) => (s.data || []).filter((v) => typeof v === 'number'))
      );
      return withIntegerAxis(withDateAxis(barOptions()), max);
    },
    unwitnessedOptions() {
      const options = withoutLegend(withPercentAxis(barOptions({ horizontal: true }), { horizontal: true }));

      return {
        ...options,
        // The share printed at each bar's tip, so the panel reads without the
        // axis and without the tooltip - and so the three lighter series
        // colours are never the only way to a value. Zeros are labelled here:
        // several transaction types are always witnessed, and a bare row left
        // them looking like a panel that had failed to load.
        ...endLabels({ horizontal: true, labelZero: true, formatter: (v) => `${v}%` }),
        grid: { ...options.grid, padding: { ...options.grid.padding, right: 44 } },
      };
    },
    /**
     * Apex draws series[0] along the bottom of a heatmap, which put the
     * biggest drug last on a panel titled "top drugs". Reversed so the rows
     * read down the card in the order the endpoint ranked them.
     */
    drugWeekSeries() {
      return this.panels.drugWeeks.series.slice().reverse();
    },
    heatmapChartOptions() {
      const max = Math.max(
        1,
        ...this.panels.drugWeeks.series.flatMap((s) => s.data.filter((v) => typeof v === 'number'))
      );
      return heatmapOptions(max);
    },
  },
  methods: {
    fetchAll() {
      if (!this.dateRange || !this.dateRange[0]) return;
      const run = ++this.runId;

      // Sequential, slow panels last: unwitnessed-share and drug-week-matrix
      // are an order of magnitude slower than the rest, and the fast four
      // should not wait behind them. See loadSequentially's doc comment.
      loadSequentially(
        [
          { key: 'summary', endpoint: '/analytics/governance-summary', assign: (d) => (this.summary = d) },
          { key: 'variance', endpoint: '/analytics/register-variance', assign: (d) => (this.panels.variance = d) },
          { key: 'movements', endpoint: '/analytics/register-movements', assign: (d) => (this.panels.movements = d) },
          { key: 'alerts', endpoint: '/analytics/alert-flow', assign: (d) => (this.panels.alerts = d) },
          { key: 'unwitnessed', endpoint: '/analytics/unwitnessed-share', assign: (d) => (this.panels.unwitnessed = d) },
          { key: 'drugWeeks', endpoint: '/analytics/drug-week-matrix', assign: (d) => (this.panels.drugWeeks = d) },
        ],
        this.dateRange,
        this.$store.state.header,
        this.loading,
        () => run !== this.runId
      );
    },
  },
  mounted() {
    if (this.canView) this.fetchAll();
  },
};
</script>
